import { NavLink, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../../Infrastructure/Store/hooks";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchMyDepragApi } from "../../../../../../Infrastructure/Api/ApiBaseCall";
import { RootState, store } from "../../../../../../Infrastructure/Store/store";
import { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { LanguageConsumer, getTranslation } from "../../../../../../Infrastructure/Internationalisation/TranslationService";
import { WhatsNewResponseDto } from "../Models/WhatsNewResponseDto";
import { WhatsNewInformation } from "../Models/WhatsNewInformation";
import { Button, Grid, IconButton, Tooltip } from "@mui/material";
import { PiTrash } from "react-icons/pi";
import { DataGridSearch } from "../../../../../../Layout/DataGrid/DataGridSearch";
import { StripedDataGrid } from "../../../../../../Layout/DataGrid/StripedDataGrid";
import { GRID_DEFAULT_LOCALE_TEXT_DE } from "../../../../../../assets/DataGridLocalisation/DataGridLocale_de";
import { GRID_DEFAULT_LOCALE_TEXT_EN } from "../../../../../../assets/DataGridLocalisation/DataGridLocale_en";
import { AddCircleOutline } from "@mui/icons-material";
import { IoPencilSharp } from "react-icons/io5";
import { setDeleteTitleAndIdWhatsNew, toggleDeletePopupWhatsNew } from "../../Store/WhatsNewSlice";
import { WhatsNewDelete } from "../../EditOrCreate/Components/DeletePopup/WhatsNewDelete";
import { DefaultDateTimeFormatOptions } from "../../../../../../Infrastructure/DateTime/DefaultDateTimeFormatOptions";

export const WhatsNewTable = () => {
    const dispatch = useAppDispatch();
    const language = useAppSelector(store => store.settings.language);
    const langIsDe = useAppSelector(store => store.settings.language === 'de');
    const { deletePopupVisible } = useAppSelector(store => store.whatsNewDelete);
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState<string>("");
    const { data, refetch, isFetching } = useQuery({
        queryKey: ["logs"],
        queryFn: async () => await fetchMyDepragApi<WhatsNewResponseDto>(
            null,
            "highlevel/whatsnew",
            'GET',
            dispatch,
            store.getState() as RootState),
    });

    useEffect(() => {
        refetch();
    }, [deletePopupVisible, refetch, language])

    const search = (inputText: string) => {
        setSearchValue(inputText);
    }

    const convertToDate = (date: Date | string) => {
        return new Date(Date.parse(date.toString() )).toLocaleDateString(langIsDe ? 'de-de' : 'en-us', { weekday: "long", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" })
    }

    const columns: GridColDef[] = [
        {
            field: 'WhatsNewInformationsId',
            headerName: "Id",
            flex: 0,
            minWidth: 0
        },
        {
            field: 'CreatedOn',
            headerName: getTranslation('HighLevelWhatsNewCreatedOn'),
            flex: 1,
            minWidth: 100,
            valueGetter: (params: GridRenderCellParams<any, WhatsNewInformation, any>) => convertToDate(params.row.CreatedOn),
            valueFormatter: (params) =>
            params.value.toLocaleString(
                langIsDe ? "de-DE" : "en-US",
                DefaultDateTimeFormatOptions as any
            ),
        },
        {
            field: 'Header',
            headerName: getTranslation('HighLevelWhatsNewHeader'),
            flex: 1,
            minWidth: 100,
            valueGetter: (params: GridRenderCellParams<any, WhatsNewInformation, any>) => langIsDe ? params.row.HeaderDe : params.row.HeaderEn,
        },
        {
            field: 'Information',
            headerName: getTranslation('HighLevelLogsRequestInformation'),
            flex: 1,
            minWidth: 500,
            valueGetter: (params: GridRenderCellParams<any, WhatsNewInformation, any>) => langIsDe ? params.row.InformationDe : params.row.InformationEn,            
        },
        {
            field: '',
            headerName: '',
            flex: 0.1,
            minWidth: 100,
            align: 'right',
            renderCell: (params: GridRenderCellParams<any, WhatsNewInformation, any>) => (
                <strong>
                    <Tooltip title={getTranslation("CommonEdit")}>
                        <IconButton color={'darky'} component={NavLink} to={`/highlevel/whatsnew/editorcreate/${params.row.WhatsNewInformationsId}`}>
                            <IoPencilSharp />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={getTranslation("CommonDelete")}>
                        <IconButton sx={{ ml: 1 }} color={'darky'} onClick={() => {
                            dispatch(setDeleteTitleAndIdWhatsNew({ title: langIsDe ? params.row.HeaderDe : params.row.HeaderEn, id: params.row.WhatsNewInformationsId }));
                            dispatch(toggleDeletePopupWhatsNew())
                        }}>
                            <PiTrash />
                        </IconButton>
                    </Tooltip>
                </strong>
            ),
            sortable: false,
            filterable: false
        }
    ]

    const handleClick = (e: GridRowParams<any>, WhatsNewInformationsId: any) => {
        navigate("/highlevel/whatsnew/editorcreate/" + WhatsNewInformationsId);
    }


    return (

        <LanguageConsumer>

            {({ getTranslatedText }) =>
                <>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12} textAlign='center'>
                            <Grid container spacing={2}>
                                <Grid item xs={4} my={'auto'} sx={{ textAlign: 'left', overflowX: 'hidden' }}>
                                    <DataGridSearch searchValue={searchValue} search={search} />
                                </Grid>
                                <Grid item xs={8} my={'auto'} sx={{ textAlign: 'right' }}>
                                    <Button
                                        startIcon={<AddCircleOutline />}
                                        sx={{ ml: 1 }}
                                        component={NavLink}
                                        to={'/highlevel/whatsnew/editorcreate/0'}
                                        variant="contained"
                                        color="success">{getTranslatedText("CreateWhatsNewEntry")}</Button>
                                </Grid>
                            </Grid>

                            <StripedDataGrid
                                localeText={langIsDe ? GRID_DEFAULT_LOCALE_TEXT_DE : GRID_DEFAULT_LOCALE_TEXT_EN}
                                rows={data ? data.WhatsNewInformations.filter(x =>
                                (x.WhatsNewInformationsId && x.WhatsNewInformationsId.toString().toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                                    x.HeaderDe && x.HeaderDe?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                                    x.HeaderEn && x.HeaderEn?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                                    convertToDate(x.CreatedOn).toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                                    x.InformationDe && x.InformationDe?.toString().toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                                    x.InformationEn && x.InformationEn?.toString().toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
                                ),) : []}
                                rowHeight={50}
                                autoHeight={true}
                                columns={columns}
                                disableColumnSelector
                                onRowDoubleClick={(e,) => handleClick(e, e.row.WhatsNewInformationsId)}
                                getRowId={(x) => x.WhatsNewInformationsId}
                                loading={isFetching}
                                getRowClassName={(params) =>
                                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                }
                            />
                        </Grid>
                    </Grid>

                    <WhatsNewDelete />
                </>
            }
        </LanguageConsumer>

    )
}